import { PersistentUserData } from '@models/persistence/PersistentUserData'
import { PersistentUserDataLayer } from '@models/persistence/PersistentUserDataLayer'
import { UserDataService } from './UserDataService'
import { toRaw } from 'vue'

export class PersistentDataService {
  /**
   * This is the version of the preferences, if the version is different
   * the preferences will be removed
   */
  static PREFERENCES_VERSION = 5

  private static getUserId() {
    return UserDataService.getUserData()?.idUsuario
  }

  private static saveData(data: PersistentUserData) {
    const userId = this.getUserId()
    data.preferencesVersion = this.PREFERENCES_VERSION
    if (userId) {
      localStorage.setItem(
        this.getUserDataKey(userId),
        btoa(JSON.stringify(toRaw(data)))
      )
    } else {
      console.error('No user id found on saveData')
    }
  }

  static getOptions<K extends keyof PersistentUserData>(
    key: K
  ): PersistentUserData[K] {
    const userData = this.getUserData()
    if (userData) {
      return userData[key]
    }
    return undefined as PersistentUserData[K]
  }

  static saveOptions<K extends keyof PersistentUserData>(
    key: K,
    options: PersistentUserData[K]
  ) {
    const userData = this.getUserData() || {
      preferencesVersion: this.PREFERENCES_VERSION
    }
    userData[key] = options
    this.saveData(toRaw(userData))
  }

  private static createUserData(): PersistentUserData {
    return {
      preferencesVersion: this.PREFERENCES_VERSION
    }
  }

  static getUserData(): PersistentUserData | undefined {
    const userId = this.getUserId()
    if (userId) {
      const data = localStorage.getItem(this.getUserDataKey(userId))
      if (data) {
        const readableData = JSON.parse(atob(data)) as PersistentUserData
        if (readableData.preferencesVersion >= this.PREFERENCES_VERSION) {
          return readableData
        } else {
          this.removeUserData(userId)
        }
      }
      return this.createUserData()
    }
  }

  static removeUserData(userId: number) {
    localStorage.removeItem(this.getUserDataKey(userId))
  }

  static updateLayerData(layerConfigData: PersistentUserDataLayer) {
    const userData = this.getUserData()
    if (userData) {
      if (userData.layers) {
        if (
          userData.layers.some(
            (layer) => layer.layerID === layerConfigData.layerID
          )
        ) {
          userData.layers = userData.layers?.map((layer) => {
            if (layer.layerID === layerConfigData.layerID) {
              return {
                ...layer,
                ...layerConfigData
              }
            }
            return layer
          })
        } else {
          userData.layers.push(layerConfigData)
        }
      } else {
        userData.layers = []
        userData.layers.push(layerConfigData)
      }
      this.saveData(toRaw(userData))
    }
  }

  static getUserDataKey(userId: number) {
    return `${userId}_userData`
  }
}
